import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ESTADOS_VE, MIEMBRO_OPTS, MUNON_OPTS, AFECTADO_OPTS, CASE_STATUSES, statusMeta, formatDate,
  NIVEL_INFERIOR_OPTS, NIVEL_SUPERIOR_OPTS,
} from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Search, Download, Filter, Loader2, X, Trash2 } from "lucide-react";

const ALL = "__all__";

export default function Patients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState("");
  const [f, setF] = useState({ miembro: "", nivel: "", estado: "", ciudad: "", case_status: "", estado_munon: "", afectado: "", fecha_desde: "", fecha_hasta: "" });
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { search, ...f };
    Object.keys(params).forEach((k) => !params[k] && delete params[k]);
    try {
      const { data } = await api.get("/patients", { params });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [search, f]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const setFilter = (k, v) => setF((prev) => ({ ...prev, [k]: v === ALL ? "" : v }));
  const clearFilters = () => { setF({ miembro: "", nivel: "", estado: "", ciudad: "", case_status: "", estado_munon: "", afectado: "", fecha_desde: "", fecha_hasta: "" }); setSearch(""); };
  const activeFilters = Object.values(f).filter(Boolean).length;

  const exportCsv = async () => {
    const params = { search, ...f };
    Object.keys(params).forEach((k) => !params[k] && delete params[k]);
    const qs = new URLSearchParams({ ...params, auth: getToken() }).toString();
    window.open(`${API}/patients/export?${qs}`, "_blank");
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/patients/${toDelete.numero_paciente}`);
      toast.success("Registro eliminado");
      setToDelete(null);
      load();
    } catch {
      toast.error("No se pudo eliminar el registro.");
    }
  };

  const nivelOpts = [...new Set([...NIVEL_INFERIOR_OPTS, ...NIVEL_SUPERIOR_OPTS])];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">Pacientes</h1>
          {data && <p className="text-slate-500 mt-1" data-testid="results-count">Mostrando {data.count} de {data.total} pacientes</p>}
        </div>
        <button onClick={exportCsv} data-testid="export-csv" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors">
          <Download className="h-4 w-4" /> Exportar CSV
        </button>
      </div>

      <div className="mt-5 flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-input" placeholder="Buscar por Nº, nombre o cédula…"
            className="w-full h-11 pl-11 pr-4 rounded-lg border border-slate-300 bg-white outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-600" />
        </div>
        <button onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-slate-300 bg-white font-medium text-slate-700 hover:bg-slate-50 relative">
          <Filter className="h-4 w-4" /> Filtros
          {activeFilters > 0 && <span className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-orange-600 text-white text-xs flex items-center justify-center">{activeFilters}</span>}
        </button>
      </div>

      {showFilters && (
        <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <FilterSelect label="Miembro afectado" value={f.miembro} onChange={(v) => setFilter("miembro", v)} options={MIEMBRO_OPTS} testId="filter-miembro" />
          <FilterSelect label="Nivel de amputación" value={f.nivel} onChange={(v) => setFilter("nivel", v)} options={nivelOpts} testId="filter-nivel" />
          <FilterSelect label="Estado (región)" value={f.estado} onChange={(v) => setFilter("estado", v)} options={ESTADOS_VE} testId="filter-estado" />
          <FilterSelect label="Estado del caso" value={f.case_status} onChange={(v) => setFilter("case_status", v)} options={CASE_STATUSES.map((s) => s.value)} labels={Object.fromEntries(CASE_STATUSES.map((s) => [s.value, s.label]))} testId="filter-case" />
          <FilterSelect label="Estado del muñón" value={f.estado_munon} onChange={(v) => setFilter("estado_munon", v)} options={MUNON_OPTS} testId="filter-munon" />
          <FilterSelect label="Afectado por terremoto" value={f.afectado} onChange={(v) => setFilter("afectado", v)} options={AFECTADO_OPTS} testId="filter-afectado" />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Ciudad</label>
            <input value={f.ciudad} onChange={(e) => setFilter("ciudad", e.target.value)} data-testid="filter-ciudad" className="w-full h-11 px-3 rounded-lg border border-slate-300 outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-600" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Desde</label>
            <input type="date" value={f.fecha_desde} onChange={(e) => setFilter("fecha_desde", e.target.value)} data-testid="filter-desde" className="w-full h-11 px-3 rounded-lg border border-slate-300 outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-600" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Hasta</label>
            <input type="date" value={f.fecha_hasta} onChange={(e) => setFilter("fecha_hasta", e.target.value)} data-testid="filter-hasta" className="w-full h-11 px-3 rounded-lg border border-slate-300 outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-600" />
          </div>
          <div className="flex items-end">
            <button onClick={clearFilters} data-testid="clear-filters" className="inline-flex items-center gap-1.5 text-slate-600 hover:text-slate-900 text-sm h-11"><X className="h-4 w-4" /> Limpiar filtros</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-600" /></div>
      ) : !data?.patients.length ? (
        <p className="text-slate-500 py-16 text-center">No se encontraron pacientes.</p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block mt-5 bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  {["Nº Paciente", "Nombre", "Edad", "Estado", "Miembro", "Nivel", "Muñón", "Registro", "Caso", ""].map((h, i) => (
                    <th key={i} className="px-4 py-3 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.patients.map((p) => {
                  const sm = statusMeta(p.case_status);
                  return (
                    <tr key={p.numero_paciente} onClick={() => navigate(`/admin/pacientes/${p.numero_paciente}`)} data-testid={`patient-row-${p.numero_paciente}`}
                      className="border-t border-slate-100 hover:bg-orange-50/40 cursor-pointer">
                      <td className="px-4 py-3 font-semibold text-orange-700 whitespace-nowrap">{p.numero_paciente}</td>
                      <td className="px-4 py-3 text-slate-900">{p.pac_nombre}</td>
                      <td className="px-4 py-3">{p.pac_edad}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{p.pac_estado}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{p.miembro_afectado}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{p.nivel_inferior || p.nivel_superior || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{p.estado_munon}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{formatDate(p.created_at)}</td>
                      <td className="px-4 py-3"><span className={`inline-block px-2.5 py-1 rounded-full text-xs border ${sm.color}`}>{sm.label}</span></td>
                      <td className="px-4 py-3">
                        {canEdit && (
                          <button onClick={(e) => { e.stopPropagation(); setToDelete(p); }} data-testid={`delete-${p.numero_paciente}`} className="text-slate-400 hover:text-red-600 p-1" title="Eliminar">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden mt-5 space-y-3">
            {data.patients.map((p) => {
              const sm = statusMeta(p.case_status);
              return (
                <div key={p.numero_paciente} onClick={() => navigate(`/admin/pacientes/${p.numero_paciente}`)} data-testid={`patient-card-${p.numero_paciente}`}
                  className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 cursor-pointer">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-orange-700">{p.numero_paciente}</span>
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs border ${sm.color}`}>{sm.label}</span>
                      {canEdit && (
                        <button onClick={(e) => { e.stopPropagation(); setToDelete(p); }} data-testid={`delete-${p.numero_paciente}`} className="text-slate-400 hover:text-red-600 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="font-medium text-slate-900 mt-1">{p.pac_nombre}</p>
                  <p className="text-sm text-slate-500 mt-1">{p.pac_edad} años · {p.pac_estado} · {p.miembro_afectado}</p>
                  <p className="text-xs text-slate-400 mt-1">Registro: {formatDate(p.created_at)}</p>
                </div>
              );
            })}
          </div>
        </>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este registro?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará permanentemente el expediente de <strong>{toDelete?.numero_paciente} — {toDelete?.pac_nombre}</strong> y sus fotografías. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-list" className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, labels, testId }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
      <Select value={value || ALL} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className="w-full h-11 bg-white"><SelectValue placeholder="Todos" /></SelectTrigger>
        <SelectContent className="bg-white max-h-72">
          <SelectItem value={ALL}>Todos</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{labels?.[o] || o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
